import React, { useState, useCallback } from 'react';
import { FaUpload, FaUserCircle, FaIdCard, FaFileAlt, FaSearchPlus, FaSearchMinus, FaCheck, FaTimes, FaEdit } from 'react-icons/fa';
import Cropper from 'react-easy-crop';
import { Modal } from './common';
import axiosInstance from '../utils/axiosConfig';
import './EmployeePhotoModal.css';

// Función helper para crear un objeto Image desde una URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Permitir CORS para canvas
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

function EmployeePhotoModal({ employee, onClose, onPhotoUpload }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(employee?.foto || null);
  const [activeTab, setActiveTab] = useState('foto'); // 'foto', 'cedula', 'rif'

  // Estados para el editor de recorte
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [encuadre, setEncuadre] = useState(null);
  const [encuadrando, setEncuadrando] = useState(false);

  // Abre el editor ya encuadrado en el rostro: GH suele confirmar sin tocar el zoom
  const abrirEditor = async (src) => {
    setImageSrc(src);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setEncuadre(null);
    setEncuadrando(true);
    try {
      const blob = await (await fetch(src)).blob();
      const fd = new FormData();
      fd.append('file', blob, 'foto.jpg');
      const { data } = await axiosInstance.post('/api/empleados/foto/encuadre', fd);
      setEncuadre(data.encuadre);
    } catch (error) {
      console.error('No se pudo encuadrar la foto:', error);
    } finally {
      setEncuadrando(false);
      setIsEditing(true);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      // Crear preview para el editor
      const reader = new FileReader();
      reader.onloadend = () => {
        if (activeTab === 'foto') {
          abrirEditor(reader.result);
          return;
        }
        setImageSrc(reader.result);
        setIsEditing(true);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Función para crear la imagen recortada
  const createCroppedImage = async () => {
    try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Configurar el tamaño del canvas según el área recortada
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      // Dibujar la imagen recortada
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      // Convertir canvas a blob
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.95);
      });
    } catch (error) {
      console.error('Error al recortar la imagen:', error);
      return null;
    }
  };

  const handleConfirmCrop = async () => {
    if (!croppedAreaPixels) return;

    const croppedBlob = await createCroppedImage();
    if (croppedBlob) {
      // Crear un nuevo archivo con la imagen recortada
      const croppedFile = new File([croppedBlob], selectedFile.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Crear preview de la imagen recortada
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(croppedBlob);

      // Actualizar el archivo seleccionado
      setSelectedFile(croppedFile);
      setIsEditing(false);
    }
  };

  const handleCancelCrop = () => {
    setIsEditing(false);
    setImageSrc(null);
    setSelectedFile(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const handleUpload = async () => {
    if (selectedFile) {
      await onPhotoUpload(employee.id, selectedFile, activeTab);
      onClose();
    }
  };

  // Función para editar la foto existente
  const handleEditExistingPhoto = () => {
    if (!previewUrl) return;

    // Usamos la URL directamente con el Cropper
    // El archivo se creará cuando se confirme el recorte
    setSelectedFile(new File([], 'foto_recortada.jpg', { type: 'image/jpeg' }));
    abrirEditor(previewUrl);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'foto':
        return (
          <div className="photo-container">
            {isEditing ? (
              // Editor de recorte
              <div className="crop-container-wrapper">
                {/* Con rostro detectado el encuadre es fijo: GH solo confirma */}
                <div className={`crop-container ${encuadre ? 'crop-fijo' : ''}`}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    initialCroppedAreaPixels={encuadre || undefined}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    zoomWithScroll={!encuadre}
                  />
                </div>

                <div className="crop-controls">
                  {encuadre ? (
                    <p className="crop-hint">Encuadre automático en el rostro</p>
                  ) : (
                  <div className="zoom-control">
                    <FaSearchMinus />
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="zoom-slider"
                    />
                    <FaSearchPlus />
                  </div>
                  )}
                  {!encuadre && (
                    <p className="crop-hint">No se detectó un rostro. Arrastra para posicionar y usa el slider para hacer zoom</p>
                  )}
                </div>

                <div className="crop-actions">
                  <button className="cancel-btn" onClick={handleCancelCrop}>
                    <FaTimes /> Cancelar
                  </button>
                  <button className="confirm-btn" onClick={handleConfirmCrop}>
                    <FaCheck /> Confirmar recorte
                  </button>
                </div>
              </div>
            ) : (
              // Vista previa normal
              <>
                <div
                  className={`photo-preview ${previewUrl ? 'editable' : ''}`}
                  onClick={previewUrl ? handleEditExistingPhoto : undefined}
                  title={previewUrl ? 'Clic para editar' : undefined}
                >
                  {previewUrl ? (
                    <>
                      <img src={previewUrl} alt={employee.nombre} className="preview-img" />
                      <div className="photo-edit-overlay">
                        <FaEdit />
                        <span>Editar</span>
                      </div>
                    </>
                  ) : (
                    <FaUserCircle className="preview-placeholder" />
                  )}
                </div>

                <div className="photo-actions">
                  <label className="upload-btn">
                    <FaUpload /> {encuadrando ? 'Buscando el rostro...' : 'Seleccionar foto'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {selectedFile && !isEditing && (
                    <button className="save-btn" onClick={handleUpload}>
                      Guardar foto
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );

      case 'cedula':
        const isPdfCedula = employee.cedula_url?.toLowerCase().includes('.pdf');
        return (
          <div className="document-container">
            <div className="document-preview">
              {employee.cedula_url ? (
                isPdfCedula ? (
                  <iframe
                    src={employee.cedula_url}
                    title="Cédula PDF"
                    className="document-pdf"
                  />
                ) : (
                  <img src={employee.cedula_url} alt="Cédula" className="document-img" />
                )
              ) : (
                <div className="document-placeholder">
                  <FaIdCard />
                  <p>No hay cédula cargada</p>
                </div>
              )}
            </div>

            {!employee.cedula_url && (
              <div className="photo-actions">
                <label className="upload-btn">
                  <FaUpload /> Cargar cédula
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </label>

                {selectedFile && (
                  <button className="save-btn" onClick={handleUpload}>
                    Guardar cédula
                  </button>
                )}
              </div>
            )}
          </div>
        );

      case 'rif':
        const isPdfRif = employee.rif_url?.toLowerCase().includes('.pdf');
        return (
          <div className="document-container">
            <div className="document-preview">
              {employee.rif_url ? (
                isPdfRif ? (
                  <iframe
                    src={employee.rif_url}
                    title="RIF PDF"
                    className="document-pdf"
                  />
                ) : (
                  <img src={employee.rif_url} alt="RIF" className="document-img" />
                )
              ) : (
                <div className="document-placeholder">
                  <FaFileAlt />
                  <p>No hay RIF cargado</p>
                </div>
              )}
            </div>

            {!employee.rif_url && (
              <div className="photo-actions">
                <label className="upload-btn">
                  <FaUpload /> Cargar RIF
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </label>

                {selectedFile && (
                  <button className="save-btn" onClick={handleUpload}>
                    Guardar RIF
                  </button>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal isOpen={!!employee} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>{employee?.nombre}</Modal.Header>
      <Modal.Body>
        <div className="employee-photo-tabs">
          <button
            className={`tab-btn ${activeTab === 'foto' ? 'active' : ''}`}
            onClick={() => setActiveTab('foto')}
          >
            <FaUserCircle /> Foto
          </button>
          <button
            className={`tab-btn ${activeTab === 'cedula' ? 'active' : ''}`}
            onClick={() => setActiveTab('cedula')}
          >
            <FaIdCard /> Cédula
          </button>
          <button
            className={`tab-btn ${activeTab === 'rif' ? 'active' : ''}`}
            onClick={() => setActiveTab('rif')}
          >
            <FaFileAlt /> RIF
          </button>
        </div>

        <div className="employee-photo-content">
          {renderContent()}
        </div>

        <div className="employee-photo-info mt-4 pt-4 border-t border-gray-200">
          <p><strong>Ficha:</strong> {employee?.ficha}</p>
          <p><strong>Cédula:</strong> {employee?.cedula || 'No registrada'}</p>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default EmployeePhotoModal;
